import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  AlertTriangle,
  Pill,
  Activity,
  ArrowRight,
  HeartPulse,
  Loader2,
  RefreshCw,
  Sparkles,
} from "lucide-react";
import type { ActiveAlertInfo } from "@/components/trainingplan/HealthProfileSummaryCard";

interface HealthProfileStepProps {
  patientName: string;
  patientAge?: number | null;
  healthSummary: string;
  healthScore?: number | null;
  conditions: string[];
  problems: string[];
  activeAlerts: ActiveAlertInfo[];
  medications: string[];
  loading: boolean;
  hasAnalysis?: boolean;
  onContinue: () => void;
  onRegenerate?: () => void;
  onGenerateAnalysis?: () => void;
  regenerating?: boolean;
}

const severityVariant = (severity?: string): "destructive" | "secondary" | "outline" => {
  const s = (severity || "").toLowerCase();
  if (s === "critical" || s === "high" || s === "risco_alto") return "destructive";
  if (s === "warning" || s === "atencao" || s === "medium") return "secondary";
  return "outline";
};

const scoreColor = (score: number | null | undefined) => {
  if (typeof score !== "number") return "outline";
  if (score >= 75) return "default";
  if (score >= 50) return "secondary";
  return "destructive";
};

export function HealthProfileStep({
  patientName,
  patientAge,
  healthSummary,
  healthScore,
  conditions,
  problems,
  activeAlerts,
  medications,
  loading,
  hasAnalysis = true,
  onContinue,
  onRegenerate,
  onGenerateAnalysis,
  regenerating = false,
}: HealthProfileStepProps) {
  const visibleProblems = (problems.length > 0 ? problems : conditions).slice(0, 8);
  const visibleAlerts = activeAlerts.slice(0, 5);
  const visibleMeds = medications.slice(0, 6);
  const hasAnything =
    visibleProblems.length > 0 || visibleAlerts.length > 0 || visibleMeds.length > 0;

  return (
    <Card className="border-border/60">
      <CardHeader>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-primary/10 text-primary">
              <HeartPulse className="size-5" />
            </div>
            <div className="flex flex-col gap-1">
              <CardTitle className="text-xl">Perfil de saúde</CardTitle>
              <p className="text-sm text-muted-foreground">
                Informações relevantes para personalizar seu treino com segurança.
              </p>
            </div>
          </div>
          {typeof healthScore === "number" ? (
            <Badge variant={scoreColor(healthScore) as "default" | "secondary" | "destructive" | "outline"}>
              Health Score {healthScore}
            </Badge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-5">
        <div className="rounded-lg border border-border/60 bg-muted/30 p-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-sm text-muted-foreground">
                <span className="font-medium text-foreground">{patientName}</span>
                {typeof patientAge === "number" ? <> · {patientAge} anos</> : null}
              </div>
              {hasAnalysis ? (
                <p className="text-sm mt-1 leading-relaxed">{healthSummary}</p>
              ) : (
                <p className="text-sm mt-1 leading-relaxed text-muted-foreground">
                  Este paciente ainda não possui uma análise de saúde gerada. Clique no botão
                  abaixo para gerar uma análise personalizada com IA.
                </p>
              )}
            </div>
            {hasAnalysis && onRegenerate ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={onRegenerate}
                disabled={regenerating || loading}
                aria-label="Regenerar análise com IA"
                title="Regenerar análise com IA"
                className="shrink-0"
              >
                {regenerating ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <RefreshCw className="size-4" />
                )}
                <span className="ml-1 hidden sm:inline">
                  {regenerating ? "Regerando..." : "Regenerar análise"}
                </span>
              </Button>
            ) : null}
          </div>

          {!hasAnalysis && onGenerateAnalysis ? (
            <div className="mt-3">
              <Button
                type="button"
                variant="default"
                size="sm"
                onClick={onGenerateAnalysis}
                disabled={regenerating || loading}
              >
                {regenerating ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Sparkles className="size-4" />
                )}
                <span className="ml-1">
                  {regenerating ? "Gerando análise..." : "Gerar análise com IA"}
                </span>
              </Button>
            </div>
          ) : null}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="size-4 animate-spin" />
            Analisando seu perfil de saúde...
          </div>
        ) : (
          <>
            {visibleProblems.length > 0 && (
              <section className="flex flex-col gap-2">
                <div className="flex items-center gap-2 text-sm font-medium">
                  <Activity className="size-4 text-primary" />
                  Condições relevantes para treino
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {visibleProblems.map((p) => (
                    <Badge key={p} variant="secondary" className="font-normal">
                      {p}
                    </Badge>
                  ))}
                </div>
              </section>
            )}

            {visibleAlerts.length > 0 && (
              <>
                <Separator />
                <section className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <AlertTriangle className="size-4 text-destructive" />
                    Alertas e restrições
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {visibleAlerts.map((a, i) => (
                      <Badge
                        key={`${a.title}-${i}`}
                        variant={severityVariant(a.severity)}
                        className="font-normal"
                        title={a.message}
                      >
                        {a.title || a.message || "Alerta"}
                      </Badge>
                    ))}
                  </div>
                </section>
              </>
            )}

            {visibleMeds.length > 0 && (
              <>
                <Separator />
                <section className="flex flex-col gap-2">
                  <div className="flex items-center gap-2 text-sm font-medium">
                    <Pill className="size-4 text-primary" />
                    Medicamentos com impacto no exercício
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {visibleMeds.map((m) => (
                      <Badge key={m} variant="outline" className="font-normal">
                        {m}
                      </Badge>
                    ))}
                  </div>
                </section>
              </>
            )}

            {!hasAnything && hasAnalysis && (
              <p className="text-sm text-muted-foreground">
                Sem condições, alertas ou medicamentos relevantes registrados. Vamos seguir para
                seus objetivos.
              </p>
            )}
          </>
        )}

        <div className="flex justify-end pt-2">
          <Button onClick={onContinue} disabled={loading} size="lg">
            Continuar
            <ArrowRight data-icon="inline-end" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
